import { useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, UserCheck } from 'lucide-react';
import { Input } from '@repo/ui/src/components/ui/input';
import { Button } from '@repo/ui/src/components/ui/button';
import { cn } from '@repo/ui/src/lib/utils';
import { useStudents } from '../../../hooks/useStudents';
import { useClientOptions } from '../../../hooks/useClients';
import { ClientCombobox } from '../../../components/ClientCombobox';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { useDebouncedValue } from './useDebouncedValue';
import type { SourceTabProps } from './types';
import type { DraftMemberRow } from './confirmRows';

const digitsOf = (s: string) => s.replace(/\D/g, '');

type PayerMode = 'newPayer' | 'existingClient' | 'self';

export interface CreateRowState {
  key: string;
  name: string;
  nickname: string;
  dateOfBirth: string;
  payerMode: PayerMode;
  payerName: string;
  payerPhone: string;
  existingClientId: string;
  /** Duplicate resolution. Defaults to "Dùng học viên đã có" for a STRONG
   *  match (name+phone, or name+the chosen existing payer) and to "Tạo mới"
   *  for a WEAK one (self-pay, name alone) — reset whenever a NEW match
   *  (different studentId) is found, per row. Meaningless while `match` is
   *  null for this row. */
  useExisting: boolean;
}

/** `strength: 'strong'` — name+payer-phone (newPayer) or name+the picked
 *  existing client (existingClient): two independent signals agree, safe to
 *  default to "Dùng học viên đã có". `'weak'` — self-pay mode matches on
 *  name ALONE, and only when the search returns EXACTLY ONE hit (fix round
 *  1, item 3): a bare-name match is not enough signal to default to reusing
 *  a profile, so it defaults to "Tạo mới" and reads "Có thể trùng với …"
 *  rather than "Đã có: …". */
export interface DupMatch {
  studentId: string;
  studentName: string;
  payerName: string;
  payerPhone: string | null;
  subjectNames: string[];
  strength: 'strong' | 'weak';
}

export interface CreateTabState {
  rows: CreateRowState[];
  matches: Record<string, DupMatch | null>;
}

let rowSeq = 0;
function emptyRow(): CreateRowState {
  rowSeq += 1;
  return {
    key: `new-${rowSeq}`,
    name: '',
    nickname: '',
    dateOfBirth: '',
    payerMode: 'newPayer',
    payerName: '',
    payerPhone: '',
    existingClientId: '',
    useExisting: true,
  };
}

/** `AddMembersDialog`'s initial/reset value for `CreateTab`'s lifted state —
 *  one blank row, no matches. */
// eslint-disable-next-line react-refresh/only-export-components -- shared factory, not a component
export function initialCreateTabState(): CreateTabState {
  return { rows: [emptyRow()], matches: {} };
}

/** One row card: Họ và tên / Tên thường gọi / Ngày sinh, then Người trả phí
 *  / SĐT-Zalo with the "Chọn người trả phí có sẵn" / "Học viên tự trả"
 *  links (S6/2), then — when a debounced match hits — the inline duplicate
 *  block (decision 8). Owns its own duplicate lookup (a `useStudents` call
 *  per row, since hooks can't be called a variable number of times from the
 *  list above) and reports the match up via `onMatchChange` so CreateTab
 *  can resolve the row to `{ studentId }` on Tiếp tục.
 *
 *  Fix round 1 (item 3): the lookup now runs in every payer mode, not just
 *  "newPayer" — matched signal differs per mode (see `DupMatch.strength`). */
function CreateRowCard({
  row, onChange, onRemove, canRemove, onMatchChange, subjectName,
}: {
  row: CreateRowState;
  onChange: (patch: Partial<CreateRowState>) => void;
  onRemove: () => void;
  canRemove: boolean;
  onMatchChange: (match: DupMatch | null) => void;
  subjectName: string;
}) {
  const { t } = useTranslation('scheduling');
  const { data: clients } = useClientOptions();
  const idPrefix = useId();

  const debouncedName = useDebouncedValue(row.name.trim(), 400);
  const debouncedPhone = useDebouncedValue(row.payerPhone.trim(), 400);
  const lookupEnabled =
    debouncedName.length >= 2 &&
    (row.payerMode === 'newPayer' ? digitsOf(debouncedPhone).length >= 6
      : row.payerMode === 'existingClient' ? Boolean(row.existingClientId)
        : true); // 'self' — name alone
  const { data } = useStudents({ search: debouncedName, pageSize: 10 }, { enabled: lookupEnabled });

  const match: DupMatch | null = useMemo(() => {
    if (!lookupEnabled || !data) return null;
    const toMatch = (hit: (typeof data.items)[number], strength: DupMatch['strength']): DupMatch => ({
      studentId: hit.id,
      studentName: hit.name,
      payerName: hit.client?.name ?? '',
      payerPhone: hit.client?.zaloPhone ?? null,
      subjectNames: (hit.subjects ?? []).map((s) => s.name),
      strength,
    });
    if (row.payerMode === 'newPayer') {
      const phoneDigits = digitsOf(debouncedPhone);
      const hit = data.items.find((s) => digitsOf(s.client?.zaloPhone ?? '') === phoneDigits);
      return hit ? toMatch(hit, 'strong') : null;
    }
    if (row.payerMode === 'existingClient') {
      const hit = data.items.find((s) => s.client?.id === row.existingClientId);
      return hit ? toMatch(hit, 'strong') : null;
    }
    // 'self': a bare-name match is weak signal — only offer it when the
    // search is unambiguous (exactly one hit).
    return data.items.length === 1 ? toMatch(data.items[0]!, 'weak') : null;
  }, [lookupEnabled, data, debouncedPhone, row.payerMode, row.existingClientId]);

  // Fix wave (review I8): report by IDENTITY (student+strength), not by
  // re-running `onMatchChange` on every render — but without the
  // whole-component `react-hooks/exhaustive-deps` disable that used to sit
  // here (§6.10: one such comment silences every react-hooks rule for this
  // entire component, including the rules that were catching real bugs
  // elsewhere in it — see C1). `matchKey` is the one primitive the SECOND
  // effect depends on; `match`/`onMatchChange` are read through refs kept
  // current by the FIRST effect (never mutated during render — the
  // react-compiler purity rule `useServerNow.ts` names for the same
  // pattern) — so both effects' dep arrays are complete and real, no
  // disable needed.
  const matchKey = match ? `${match.studentId}:${match.strength}` : '';
  const matchRef = useRef(match);
  const onMatchChangeRef = useRef(onMatchChange);
  useEffect(() => {
    matchRef.current = match;
    onMatchChangeRef.current = onMatchChange;
  });
  useEffect(() => {
    onMatchChangeRef.current(matchRef.current);
  }, [matchKey]);

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-surface-200 bg-surface-50 p-3">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.2fr] gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-surface-600">{t('addMembers.create.name')}</span>
          <Input value={row.name} onChange={(e) => onChange({ name: e.target.value })} placeholder={t('addMembers.create.namePlaceholder')} className="h-[30.4px]" />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-surface-600">{t('addMembers.create.nickname')}</span>
          <Input value={row.nickname} onChange={(e) => onChange({ nickname: e.target.value })} placeholder={t('addMembers.create.nicknamePlaceholder')} className="h-[30.4px]" />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-surface-600">{t('addMembers.create.dob')}</span>
          <DatePicker value={row.dateOfBirth || null} onChange={(v) => onChange({ dateOfBirth: v ?? '' })} className="h-[30.4px] w-full justify-between font-normal" />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.2fr] gap-2 items-end">
        {row.payerMode === 'existingClient' ? (
          <div className="flex flex-col gap-0.5 sm:col-span-2">
            <span className="text-xs font-medium text-surface-600">{t('addMembers.create.payer')}</span>
            <ClientCombobox
              options={clients ?? []}
              value={row.existingClientId}
              onSelect={(id) => onChange({ existingClientId: id })}
              placeholder="—"
              searchPlaceholder={t('addMembers.create.payerSearchPlaceholder')}
              emptyText={t('addMembers.create.payerEmpty')}
            />
          </div>
        ) : row.payerMode === 'self' ? (
          <div className="flex items-center h-[30.4px] text-[13.5px] text-surface-600 sm:col-span-2">
            {t('addMembers.create.selfPayNote')}
          </div>
        ) : (
          <>
            <label className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-surface-600">{t('addMembers.create.payer')}</span>
              <Input value={row.payerName} onChange={(e) => onChange({ payerName: e.target.value })} placeholder={t('addMembers.create.payerPlaceholder')} className="h-[30.4px]" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-surface-600">{t('addMembers.create.phone')}</span>
              <Input value={row.payerPhone} onChange={(e) => onChange({ payerPhone: e.target.value })} className="h-[30.4px] tabular" />
            </label>
          </>
        )}
        <div className="flex items-center gap-2.5 h-[30.4px] text-xs">
          {row.payerMode !== 'newPayer' && (
            <button type="button" className="font-medium text-brand-600" onClick={() => onChange({ payerMode: 'newPayer' })}>
              {t('addMembers.create.newPayerLink')}
            </button>
          )}
          {row.payerMode !== 'existingClient' && (
            <button type="button" className="font-medium text-brand-600" onClick={() => onChange({ payerMode: 'existingClient' })}>
              {t('addMembers.create.existingPayerLink')}
            </button>
          )}
          {row.payerMode !== 'self' && (
            <button type="button" className="font-medium text-brand-600" onClick={() => onChange({ payerMode: 'self' })}>
              {t('addMembers.create.selfPayLink')}
            </button>
          )}
        </div>
      </div>

      {match && (
        <div className="flex flex-col gap-1.5 rounded-lg border border-amber-400/40 bg-amber-50 px-3 py-2.5">
          <div className="flex items-start gap-2 text-[13px] text-amber-900">
            <UserCheck className="h-[15px] w-[15px] mt-0.5 shrink-0" />
            <span>
              <b className="font-semibold">
                {t(match.strength === 'strong' ? 'addMembers.create.dupFound' : 'addMembers.create.weakDupFound', { name: match.studentName, payer: match.payerName })}
              </b>
              {match.payerPhone ? ` (${match.payerPhone})` : ''}
              {match.subjectNames.length > 0 ? ` · ${t('addMembers.create.dupStudying', { subjects: match.subjectNames.join(', ') })}` : ''}
            </span>
          </div>
          <div className="flex gap-4 pl-[23px] text-[13px]">
            <label className={cn('inline-flex items-center gap-1.5 cursor-pointer', row.useExisting ? 'font-medium text-surface-800' : 'text-surface-600')}>
              <input type="radio" name={`${idPrefix}-dup`} checked={row.useExisting} onChange={() => onChange({ useExisting: true })} />
              {t('addMembers.create.useExisting')}
            </label>
            <label className={cn('inline-flex items-center gap-1.5 cursor-pointer', !row.useExisting ? 'font-medium text-surface-800' : 'text-surface-600')}>
              <input type="radio" name={`${idPrefix}-dup`} checked={!row.useExisting} onChange={() => onChange({ useExisting: false })} />
              {t('addMembers.create.createNewInstead')}
            </label>
          </div>
          {row.useExisting && (
            <p className="m-0 pl-[23px] text-xs text-amber-900/80">
              {t('addMembers.create.useExistingHint', { subject: subjectName })}
            </p>
          )}
        </div>
      )}

      {canRemove && (
        <button type="button" onClick={onRemove} className="self-end text-xs font-medium text-surface-500 hover:text-red-600">
          {t('addMembers.create.removeRow')}
        </button>
      )}
    </div>
  );
}

type CreateTabStateUpdater = CreateTabState | ((prev: CreateTabState) => CreateTabState);

interface CreateTabProps extends SourceTabProps {
  state: CreateTabState;
  onStateChange: (next: CreateTabStateUpdater) => void;
}

/** Fix round 1 (item 2): `rows`/`matches` are CONTROLLED by `AddMembersDialog`
 *  (`state`/`onStateChange`) instead of local `useState` — so switching tabs,
 *  or going to the confirm table and back via "Quay lại", keeps every typed
 *  row and every resolved duplicate. Only closing the dialog resets it
 *  (`AddMembersDialog` re-seeds `state` from `initialCreateTabState()` then). */
export function CreateTab({
  klass, existingBySubject, state, onStateChange, onContinue, onCancel,
}: CreateTabProps) {
  const { t } = useTranslation('scheduling');
  const { rows, matches } = state;

  // Re-review fix (new defect 5) — every writer below now goes through
  // `onStateChange` as a FUNCTIONAL updater (`onStateChange` is literally
  // `AddMembersDialog`'s `setCreateState`, a real `Dispatch`, so this reads
  // the truly latest state at APPLICATION time). C1's fix merged the two
  // writes inside one row's update into one call, but every writer still
  // closed over `rows`/`matches` from this render's `state` PROP — so two
  // rows whose duplicate lookups resolved inside the same React batch each
  // computed `nextMatches` from the SAME pre-batch snapshot, and the
  // second call's snapshot of the OTHER row's match silently dropped it.
  const setRows = (updater: CreateRowState[] | ((prev: CreateRowState[]) => CreateRowState[])) => {
    onStateChange((prev) => ({
      ...prev,
      rows: typeof updater === 'function' ? (updater as (prev: CreateRowState[]) => CreateRowState[])(prev.rows) : updater,
    }));
  };

  const patchRow = (key: string, patch: Partial<CreateRowState>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const removeRow = (key: string) => {
    onStateChange((prev) => {
      const nextMatches = { ...prev.matches };
      delete nextMatches[key];
      return { rows: prev.rows.filter((r) => r.key !== key), matches: nextMatches };
    });
  };

  const usable = rows.filter((r) => r.name.trim().length > 0);
  const resolvesToExisting = (r: CreateRowState) => Boolean(matches[r.key]) && r.useExisting;
  const existingCount = usable.filter(resolvesToExisting).length;
  const newCount = usable.length - existingCount;

  const canContinue = usable.length > 0 && usable.every((r) => {
    if (resolvesToExisting(r)) return true; // payer/dob irrelevant — the row resolves to an existing profile untouched
    if (r.payerMode === 'self') return true;
    if (r.payerMode === 'existingClient') return Boolean(r.existingClientId);
    return r.payerName.trim().length > 0;
  });

  const handleContinue = () => {
    const drafts: DraftMemberRow[] = usable.map((r) => {
      const match = matches[r.key] ?? null;
      if (match && r.useExisting) {
        return {
          key: r.key,
          studentId: match.studentId,
          studentName: match.studentName,
          newStudent: null,
          existingEnrollment: existingBySubject.get(match.studentId) ?? null,
          rate: '',
          repriceRequested: false,
          slotIds: null,
        };
      }
      const payer = r.payerMode === 'self'
        ? { mode: 'self' as const }
        : r.payerMode === 'existingClient'
          ? { mode: 'existingClient' as const, clientId: r.existingClientId }
          : { mode: 'newPayer' as const, name: r.payerName.trim(), phone: r.payerPhone.trim() || null };
      return {
        key: r.key,
        studentId: null,
        studentName: r.name.trim(),
        newStudent: { nickname: r.nickname.trim() || null, dateOfBirth: r.dateOfBirth || null, payer },
        existingEnrollment: null,
        rate: '',
        repriceRequested: false,
        slotIds: null,
      };
    });
    onContinue(drafts);
  };

  return (
    // Fix round 1 (item 1): the row list is the ONLY scroll container; the
    // footer is a `shrink-0` sibling pinned to the bottom of whatever height
    // the parent hands this component (bounded on the 390 Drawer, unbounded
    // — so effectively a no-op — inside the desktop Dialog's own scroll).
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2.5 pr-0.5">
        {rows.map((row) => (
          <CreateRowCard
            key={row.key}
            row={row}
            onChange={(p) => patchRow(row.key, p)}
            onRemove={() => removeRow(row.key)}
            canRemove={rows.length > 1}
            onMatchChange={(m) => {
              // Fix wave (review C1) + re-review fix (new defect 5): ONE
              // FUNCTIONAL `onStateChange` call for both the match and
              // (when it's a freshly-found match) the row's radio default,
              // read from `prev` — not the `rows`/`matches` this render's
              // props closed over — so two rows resolving in the same React
              // batch each apply against the OTHER's already-applied write
              // instead of a shared stale snapshot.
              onStateChange((prev) => {
                const prevMatch = prev.matches[row.key] ?? null;
                const isNew = (prevMatch?.studentId ?? null) !== (m?.studentId ?? null) || prevMatch?.strength !== m?.strength;
                if (prevMatch === m && !isNew) return prev;
                const nextMatches = { ...prev.matches, [row.key]: m };
                // A freshly-found match resets the radio to its own default
                // (strong -> "Dùng học viên đã có", weak -> "Tạo mới") — but
                // only when the match itself is new, never overriding a
                // choice the teacher already made for THIS match.
                const nextRows = (m && isNew)
                  ? prev.rows.map((r) => (r.key === row.key ? { ...r, useExisting: m.strength === 'strong' } : r))
                  : prev.rows;
                return { rows: nextRows, matches: nextMatches };
              });
            }}
            subjectName={klass.subject.name}
          />
        ))}
        <Button type="button" variant="ghost" size="sm" className="self-start gap-1.5 text-brand-600 px-0" onClick={() => setRows((prev) => [...prev, emptyRow()])}>
          <Plus className="h-3.5 w-3.5" /> {t('addMembers.create.addRow')}
        </Button>
      </div>

      <div className="shrink-0 flex items-center justify-between gap-2 border-t border-surface-200 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
        <span className="text-[13px] text-surface-600">
          {t('addMembers.create.footerCount', { newCount, existingCount })}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>{t('addMembers.cancel')}</Button>
          <Button type="button" disabled={!canContinue} onClick={handleContinue}>{t('addMembers.continue')}</Button>
        </div>
      </div>
    </div>
  );
}
